import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";

interface FieldFrame {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Hide the label visually (it stays for screen readers). */
  hideLabel?: boolean;
  full?: boolean;
  className?: string;
}

function Frame({
  id,
  label,
  hint,
  error,
  hideLabel,
  full,
  className = "",
  children,
}: FieldFrame & { id: string; children: ReactNode }) {
  return (
    <div className={`ds-field${full ? " ds-field--full" : ""} ${className}`.trim()}>
      <label htmlFor={id} className={hideLabel ? "ds-visually-hidden" : "ds-field__label"}>
        {label}
      </label>
      {children}
      {error ? (
        <span id={`${id}-error`} className="ds-field__error" role="alert">{error}</span>
      ) : hint ? (
        <span id={`${id}-hint`} className="ds-field__hint">{hint}</span>
      ) : null}
    </div>
  );
}

function describedBy(id: string, hint?: ReactNode, error?: ReactNode): string | undefined {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}

export function TextField({
  label,
  hint,
  error,
  hideLabel,
  full,
  className,
  id: idProp,
  ...props
}: FieldFrame & Omit<InputHTMLAttributes<HTMLInputElement>, "className">) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <Frame id={id} label={label} hint={hint} error={error} hideLabel={hideLabel} full={full} className={className}>
      <input
        id={id}
        className="ds-input"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        {...props}
      />
    </Frame>
  );
}

export function SelectField({
  label,
  hint,
  error,
  hideLabel,
  full,
  className,
  id: idProp,
  children,
  ...props
}: FieldFrame & Omit<SelectHTMLAttributes<HTMLSelectElement>, "className">) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <Frame id={id} label={label} hint={hint} error={error} hideLabel={hideLabel} full={full} className={className}>
      <select id={id} className="ds-select" aria-describedby={describedBy(id, hint, error)} {...props}>
        {children}
      </select>
    </Frame>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  hideLabel,
  full,
  className,
  id: idProp,
  ...props
}: FieldFrame & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className">) {
  const generated = useId();
  const id = idProp ?? generated;
  return (
    <Frame id={id} label={label} hint={hint} error={error} hideLabel={hideLabel} full={full} className={className}>
      <textarea
        id={id}
        className="ds-textarea"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        {...props}
      />
    </Frame>
  );
}
